import { PERMISSIONS, ALL_PERMISSIONS } from '@constants/permissions'

export const SYSTEM_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN:       'admin',
  USER:        'user',
}

export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin:       'Admin',
  user:        'User',
}

export const ROLE_DESCRIPTIONS = {
  super_admin: 'Full access to all features and settings including role management.',
  admin:       'Full access to events, assignments, users, connections, reports, and communication.',
  user:        'Access to own dashboard, assigned events, and personal connections.',
}

export const ROLE_COLORS = {
  super_admin: { color: '#7C3AED', bg: 'rgba(124,58,237,0.10)', border: '#7C3AED' },
  admin:       { color: 'var(--ac-secondary)', bg: 'var(--ac-secondary-bg, rgba(124,58,237,0.08))', border: 'var(--ac-secondary)' },
  user:        { color: 'var(--ac-text-muted)', bg: 'var(--ac-bg-subtle)', border: 'var(--ac-border-base)' },
}

export const ROLE_PERMISSIONS = {
  // Super Admin — full access to everything
  super_admin: ALL_PERMISSIONS,

  // Admin — events, assignments, users, connections, communication, reports, settings
  // Cannot access Roles & Permissions
  admin: [
    PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.DASHBOARD_MANAGE,
    PERMISSIONS.USERS_CREATE, PERMISSIONS.USERS_READ, PERMISSIONS.USERS_UPDATE,
    PERMISSIONS.USERS_DELETE, PERMISSIONS.USERS_EXPORT, PERMISSIONS.USERS_MANAGE,
    PERMISSIONS.EVENTS_CREATE, PERMISSIONS.EVENTS_READ, PERMISSIONS.EVENTS_UPDATE,
    PERMISSIONS.EVENTS_DELETE, PERMISSIONS.EVENTS_EXPORT, PERMISSIONS.EVENTS_MANAGE,
    PERMISSIONS.ASSIGNMENTS_CREATE, PERMISSIONS.ASSIGNMENTS_READ, PERMISSIONS.ASSIGNMENTS_UPDATE,
    PERMISSIONS.ASSIGNMENTS_DELETE, PERMISSIONS.ASSIGNMENTS_MANAGE,
    PERMISSIONS.CONNECTIONS_CREATE, PERMISSIONS.CONNECTIONS_READ, PERMISSIONS.CONNECTIONS_UPDATE,
    PERMISSIONS.CONNECTIONS_DELETE, PERMISSIONS.CONNECTIONS_EXPORT,
    PERMISSIONS.CONNECTIONS_VIEW_REPORTS, PERMISSIONS.CONNECTIONS_MANAGE,
    PERMISSIONS.REPORTS_READ, PERMISSIONS.REPORTS_EXPORT, PERMISSIONS.REPORTS_MANAGE,
    PERMISSIONS.NOTIFICATIONS_CREATE, PERMISSIONS.NOTIFICATIONS_READ, PERMISSIONS.NOTIFICATIONS_UPDATE,
    PERMISSIONS.NOTIFICATIONS_DELETE, PERMISSIONS.NOTIFICATIONS_MANAGE,
    PERMISSIONS.EMAIL_TEMPLATES_CREATE, PERMISSIONS.EMAIL_TEMPLATES_READ,
    PERMISSIONS.EMAIL_TEMPLATES_UPDATE, PERMISSIONS.EMAIL_TEMPLATES_DELETE,
    PERMISSIONS.SETTINGS_VIEW, PERMISSIONS.SETTINGS_UPDATE,
  ],

  // User — portal-only access
  user: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.EVENTS_READ,
    PERMISSIONS.ASSIGNMENTS_READ,
    PERMISSIONS.CONNECTIONS_CREATE, PERMISSIONS.CONNECTIONS_READ,
    PERMISSIONS.CONNECTIONS_UPDATE, PERMISSIONS.CONNECTIONS_DELETE,
    PERMISSIONS.NOTIFICATIONS_READ,
    PERMISSIONS.SETTINGS_VIEW,
  ],
}

// Permission modules used by the UI matrix (RoleForm + RoleDetailPage)
export const PERMISSION_MODULES = [
  {
    id: 'dashboard', label: 'Dashboard',
    permissions: [
      { key: PERMISSIONS.DASHBOARD_VIEW,   label: 'View'   },
      { key: PERMISSIONS.DASHBOARD_MANAGE, label: 'Manage' },
    ],
  },
  {
    id: 'users', label: 'Users',
    permissions: [
      { key: PERMISSIONS.USERS_CREATE, label: 'Create' },
      { key: PERMISSIONS.USERS_READ,   label: 'Read'   },
      { key: PERMISSIONS.USERS_UPDATE, label: 'Update' },
      { key: PERMISSIONS.USERS_DELETE, label: 'Delete' },
      { key: PERMISSIONS.USERS_EXPORT, label: 'Export' },
      { key: PERMISSIONS.USERS_MANAGE, label: 'Manage' },
    ],
  },
  {
    id: 'events', label: 'Events',
    permissions: [
      { key: PERMISSIONS.EVENTS_CREATE, label: 'Create' },
      { key: PERMISSIONS.EVENTS_READ,   label: 'Read'   },
      { key: PERMISSIONS.EVENTS_UPDATE, label: 'Update' },
      { key: PERMISSIONS.EVENTS_DELETE, label: 'Delete' },
      { key: PERMISSIONS.EVENTS_EXPORT, label: 'Export' },
      { key: PERMISSIONS.EVENTS_MANAGE, label: 'Manage' },
    ],
  },
  {
    id: 'assignments', label: 'Assignments',
    permissions: [
      { key: PERMISSIONS.ASSIGNMENTS_CREATE, label: 'Create' },
      { key: PERMISSIONS.ASSIGNMENTS_READ,   label: 'Read'   },
      { key: PERMISSIONS.ASSIGNMENTS_UPDATE, label: 'Update' },
      { key: PERMISSIONS.ASSIGNMENTS_DELETE, label: 'Delete' },
      { key: PERMISSIONS.ASSIGNMENTS_MANAGE, label: 'Manage' },
    ],
  },
  {
    id: 'connections', label: 'Connections',
    permissions: [
      { key: PERMISSIONS.CONNECTIONS_CREATE,       label: 'Create'       },
      { key: PERMISSIONS.CONNECTIONS_READ,         label: 'Read'         },
      { key: PERMISSIONS.CONNECTIONS_UPDATE,       label: 'Update'       },
      { key: PERMISSIONS.CONNECTIONS_DELETE,       label: 'Delete'       },
      { key: PERMISSIONS.CONNECTIONS_EXPORT,       label: 'Export'       },
      { key: PERMISSIONS.CONNECTIONS_VIEW_REPORTS, label: 'View Reports' },
      { key: PERMISSIONS.CONNECTIONS_MANAGE,       label: 'Manage'       },
    ],
  },
  {
    id: 'reports', label: 'Reports',
    permissions: [
      { key: PERMISSIONS.REPORTS_READ,   label: 'Read'   },
      { key: PERMISSIONS.REPORTS_EXPORT, label: 'Export' },
      { key: PERMISSIONS.REPORTS_MANAGE, label: 'Manage' },
    ],
  },
  {
    id: 'notifications', label: 'Notifications',
    permissions: [
      { key: PERMISSIONS.NOTIFICATIONS_CREATE, label: 'Create' },
      { key: PERMISSIONS.NOTIFICATIONS_READ,   label: 'Read'   },
      { key: PERMISSIONS.NOTIFICATIONS_UPDATE, label: 'Update' },
      { key: PERMISSIONS.NOTIFICATIONS_DELETE, label: 'Delete' },
      { key: PERMISSIONS.NOTIFICATIONS_MANAGE, label: 'Manage' },
    ],
  },
  {
    id: 'email_templates', label: 'Email Templates',
    permissions: [
      { key: PERMISSIONS.EMAIL_TEMPLATES_CREATE, label: 'Create' },
      { key: PERMISSIONS.EMAIL_TEMPLATES_READ,   label: 'Read'   },
      { key: PERMISSIONS.EMAIL_TEMPLATES_UPDATE, label: 'Update' },
      { key: PERMISSIONS.EMAIL_TEMPLATES_DELETE, label: 'Delete' },
    ],
  },
  {
    id: 'settings', label: 'Settings',
    permissions: [
      { key: PERMISSIONS.SETTINGS_VIEW,   label: 'View'   },
      { key: PERMISSIONS.SETTINGS_UPDATE, label: 'Update' },
      { key: PERMISSIONS.SETTINGS_MANAGE, label: 'Manage' },
    ],
  },
  {
    id: 'roles', label: 'Roles & Permissions',
    permissions: [
      { key: PERMISSIONS.ROLES_CREATE, label: 'Create' },
      { key: PERMISSIONS.ROLES_READ,   label: 'Read'   },
      { key: PERMISSIONS.ROLES_UPDATE, label: 'Update' },
      { key: PERMISSIONS.ROLES_DELETE, label: 'Delete' },
      { key: PERMISSIONS.ROLES_MANAGE, label: 'Manage' },
    ],
  },
]
